import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';

export const COLORS = {
  blue: '#8BC1F7',
  green: '#92C55E',
  amber: '#F4C145',
  red: '#F0561D',
  purple: '#B2A3FF',
  text: '#F0F0F0',
  muted: '#C7C7C7',
  border: '#4F5255',
  surface: '#1F1F1F',
};

export const healthColor = (status: string) => {
  if (status === 'Healthy') return COLORS.green;
  if (status === 'Degraded' || status === 'Missing') return COLORS.red;
  if (status === 'Progressing' || status === 'Suspended') return COLORS.amber;
  return COLORS.muted;
};

export const syncColor = (status: string) => {
  if (status === 'Synced') return COLORS.green;
  if (status === 'OutOfSync') return COLORS.amber;
  return COLORS.muted;
};

export function safeExternalUrl(value: string): string | null {
  const url = value.includes('|') ? value.split('|')[1] : value;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

export function StatusChip({ label, color }: { label: string; color: string }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 22,
        border: `1px solid ${color}35`,
        bgcolor: `${color}12`,
        color,
        fontSize: 11,
        fontWeight: 700,
      }}
    />
  );
}

export function Metric({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 145,
        p: 1.5,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        bgcolor: COLORS.surface,
      }}
    >
      <Typography sx={{ color: COLORS.muted, fontSize: 11, fontWeight: 700 }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{ mt: 0.25, color, fontSize: 24, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function Field({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '130px 1fr',
        gap: 1,
        py: 0.45,
      }}
    >
      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          minWidth: 0,
          color: COLORS.text,
          fontSize: 12.5,
          overflowWrap: 'anywhere',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

export function KeyValueChips({ values }: { values: Record<string, string> }) {
  const entries = Object.entries(values);
  if (entries.length === 0)
    return <Typography sx={{ fontSize: 12 }}>—</Typography>;
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
      {entries.map(([key, value]) => (
        <Chip
          key={key}
          size="small"
          label={`${key}=${value}`}
          title={`${key}=${value}`}
          sx={{ maxWidth: '100%', fontSize: 11 }}
        />
      ))}
    </Stack>
  );
}

export function CodePanel({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        maxHeight: 520,
        overflow: 'auto',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1,
        bgcolor: '#0b1020',
        color: COLORS.text,
        fontFamily: 'monospace',
        fontSize: 11,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {value == null
        ? 'Not available'
        : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2)}
    </Box>
  );
}
